import {
  UI_SIDEBAR_MAX_WIDTH_PX,
  UI_SIDEBAR_MIN_WIDTH_PX,
  defaultUiPreferences,
  uiPreferencesSchema,
} from "@obsina/domain";
import type { AttachmentGrouping, UiPreferences } from "@obsina/domain";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { getUiPreferences, saveUiPreferences } from "./api";

export const UI_PREFERENCES_STORAGE_KEY_PREFIX =
  "obsina.ui-preferences.v1" as const;

/**
 * The member's durable UI preferences and the commits that change them.
 *
 * The module is named for the sidebar because that is what it first carried;
 * what it actually is, and has to stay, is the one store for every preference
 * the product remembers on a member's behalf. A second store would mean a
 * second GET, a second reconcile and a second way for a late response to undo
 * a fresh choice — the whole apparatus below exists to get that right once.
 */
interface SidebarPreferencesContextValue {
  readonly preferences: UiPreferences;
  readonly setSidebarCollapsed: (collapsed: boolean) => void;
  readonly commitSidebarExpandedWidth: (widthPx: number) => void;
  /** How the collected-data list draws an item's attachments. */
  readonly setAttachmentGrouping: (grouping: AttachmentGrouping) => void;
}

const SidebarPreferencesContext =
  createContext<SidebarPreferencesContextValue | null>(null);

export interface SidebarPreferencesProviderProps {
  readonly apiBase: string | null;
  readonly userId: string | null;
  readonly children: ReactNode;
}

/**
 * Keeps sidebar chrome instant while still reconciling with the durable member
 * preference. The keyed session component prevents one signed-in user's cached
 * rail state from flashing for the next user on the same browser.
 */
export function SidebarPreferencesProvider({
  apiBase,
  userId,
  children,
}: SidebarPreferencesProviderProps) {
  return (
    <SidebarPreferencesSession
      apiBase={apiBase}
      key={userId ?? "anonymous"}
      userId={userId}
    >
      {children}
    </SidebarPreferencesSession>
  );
}

function SidebarPreferencesSession({
  apiBase,
  userId,
  children,
}: SidebarPreferencesProviderProps) {
  const storageKey = useMemo(
    () => (userId ? sidebarPreferencesStorageKey(userId) : null),
    [userId],
  );
  const initialPreferences = useMemo(
    () => readCachedPreferences(storageKey),
    [storageKey],
  );
  const [preferences, setPreferences] =
    useState<UiPreferences>(initialPreferences);
  const preferencesRef = useRef(preferences);
  const localInteractionRevisionRef = useRef(0);
  const patchQueueRef = useRef<Promise<void>>(Promise.resolve());
  const saveSessionRef = useRef(createSaveSession());

  useEffect(() => {
    const session = createSaveSession();
    saveSessionRef.current = session;
    return () => {
      session.active = false;
      session.controller.abort();
    };
  }, [apiBase, userId]);

  const applyPreferences = useCallback((next: UiPreferences) => {
    preferencesRef.current = next;
    setPreferences(next);
  }, []);

  const queueServerSave = useCallback(
    (next: UiPreferences) => {
      if (!apiBase || !userId) return;
      const session = saveSessionRef.current;
      patchQueueRef.current = patchQueueRef.current
        .catch(() => undefined)
        .then(async () => {
          // A queued snapshot belongs to the session that created it. If that
          // user signs out (or the API boundary changes) before its turn, never
          // let it run with a later user's cookie.
          if (!session.active || session.controller.signal.aborted) return;
          await saveUiPreferences(apiBase, next, session.controller.signal);
        })
        // Persistence failure must not surface as an unhandled rejection or
        // roll back the responsive local control. A later commit retries with a
        // complete snapshot, and a future mount reconciles from the server.
        .catch(() => undefined);
    },
    [apiBase, userId],
  );

  const commitLocalInteraction = useCallback(
    (next: UiPreferences) => {
      if (preferencesEqual(preferencesRef.current, next)) return;
      localInteractionRevisionRef.current += 1;
      applyPreferences(next);
      writeCachedPreferences(storageKey, next);
      queueServerSave(next);
    },
    [applyPreferences, queueServerSave, storageKey],
  );

  const setSidebarCollapsed = useCallback(
    (collapsed: boolean) => {
      commitLocalInteraction({
        ...preferencesRef.current,
        sidebarCollapsed: collapsed,
      });
    },
    [commitLocalInteraction],
  );

  const commitSidebarExpandedWidth = useCallback(
    (widthPx: number) => {
      const sidebarExpandedWidthPx = clampSidebarWidth(widthPx);
      commitLocalInteraction({
        ...preferencesRef.current,
        sidebarExpandedWidthPx,
      });
    },
    [commitLocalInteraction],
  );

  const setAttachmentGrouping = useCallback(
    (attachmentGrouping: AttachmentGrouping) => {
      commitLocalInteraction({
        ...preferencesRef.current,
        attachmentGrouping,
      });
    },
    [commitLocalInteraction],
  );

  useEffect(() => {
    if (!apiBase || !userId) return;
    const controller = new AbortController();
    const revisionAtRequestStart = localInteractionRevisionRef.current;
    void getUiPreferences(apiBase, controller.signal)
      .then((remote) => {
        // A late GET must never undo a collapse or drag that happened after it
        // started. The next PATCH already carries that newer complete snapshot.
        if (
          controller.signal.aborted ||
          localInteractionRevisionRef.current !== revisionAtRequestStart
        ) {
          return;
        }
        applyPreferences(remote);
        writeCachedPreferences(storageKey, remote);
      })
      .catch(() => {
        // The user-scoped cache remains a safe, useful fallback while offline.
      });
    return () => controller.abort();
  }, [apiBase, applyPreferences, storageKey, userId]);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    const syncFromAnotherTab = (event: StorageEvent) => {
      if (event.key !== storageKey || event.newValue === null) return;
      const incoming = parseCachedPreferences(event.newValue);
      if (!incoming || preferencesEqual(preferencesRef.current, incoming))
        return;
      // A storage event represents a newer interaction in another tab. It must
      // also protect that state from this tab's in-flight initial GET.
      localInteractionRevisionRef.current += 1;
      applyPreferences(incoming);
    };
    window.addEventListener("storage", syncFromAnotherTab);
    return () => window.removeEventListener("storage", syncFromAnotherTab);
  }, [applyPreferences, storageKey]);

  const value = useMemo<SidebarPreferencesContextValue>(
    () => ({
      preferences,
      setSidebarCollapsed,
      commitSidebarExpandedWidth,
      setAttachmentGrouping,
    }),
    [
      commitSidebarExpandedWidth,
      preferences,
      setAttachmentGrouping,
      setSidebarCollapsed,
    ],
  );

  return (
    <SidebarPreferencesContext.Provider value={value}>
      {children}
    </SidebarPreferencesContext.Provider>
  );
}

export function useSidebarPreferences(): SidebarPreferencesContextValue {
  const value = useContext(SidebarPreferencesContext);
  const [fallbackPreferences, setFallbackPreferences] = useState<UiPreferences>(
    () => ({ ...defaultUiPreferences }),
  );
  const setSidebarCollapsed = useCallback((sidebarCollapsed: boolean) => {
    setFallbackPreferences((current) => ({ ...current, sidebarCollapsed }));
  }, []);
  const commitSidebarExpandedWidth = useCallback(
    (sidebarExpandedWidthPx: number) => {
      setFallbackPreferences((current) => ({
        ...current,
        sidebarExpandedWidthPx: clampSidebarWidth(sidebarExpandedWidthPx),
      }));
    },
    [],
  );
  const setAttachmentGrouping = useCallback(
    (attachmentGrouping: AttachmentGrouping) => {
      setFallbackPreferences((current) => ({ ...current, attachmentGrouping }));
    },
    [],
  );
  const fallback = useMemo<SidebarPreferencesContextValue>(
    () => ({
      preferences: fallbackPreferences,
      setSidebarCollapsed,
      commitSidebarExpandedWidth,
      setAttachmentGrouping,
    }),
    [
      commitSidebarExpandedWidth,
      fallbackPreferences,
      setAttachmentGrouping,
      setSidebarCollapsed,
    ],
  );
  return value ?? fallback;
}

export function sidebarPreferencesStorageKey(userId: string): string {
  return `${UI_PREFERENCES_STORAGE_KEY_PREFIX}:${encodeURIComponent(userId)}`;
}

function clampSidebarWidth(widthPx: number): number {
  const rounded = Number.isFinite(widthPx)
    ? Math.round(widthPx)
    : defaultUiPreferences.sidebarExpandedWidthPx;
  return Math.min(
    UI_SIDEBAR_MAX_WIDTH_PX,
    Math.max(UI_SIDEBAR_MIN_WIDTH_PX, rounded),
  );
}

function readCachedPreferences(storageKey: string | null): UiPreferences {
  if (!storageKey || typeof window === "undefined") {
    return { ...defaultUiPreferences };
  }
  try {
    return (
      parseCachedPreferences(window.localStorage.getItem(storageKey)) ?? {
        ...defaultUiPreferences,
      }
    );
  } catch {
    return { ...defaultUiPreferences };
  }
}

function parseCachedPreferences(value: string | null): UiPreferences | null {
  if (value === null) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    const result = uiPreferencesSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function writeCachedPreferences(
  storageKey: string | null,
  preferences: UiPreferences,
): void {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preferences));
  } catch {
    // Storage may be unavailable in a hardened browser; the in-memory state and
    // authenticated server preference still work without it.
  }
}

function preferencesEqual(left: UiPreferences, right: UiPreferences): boolean {
  return (
    left.sidebarCollapsed === right.sidebarCollapsed &&
    left.sidebarExpandedWidthPx === right.sidebarExpandedWidthPx &&
    left.attachmentGrouping === right.attachmentGrouping
  );
}

interface SaveSession {
  active: boolean;
  readonly controller: AbortController;
}

function createSaveSession(): SaveSession {
  return { active: true, controller: new AbortController() };
}
